// DEPRECATED
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import {
  ColaboradorFilter,
  CourseFilter,
  DateRangeFilter,
} from './components/filters';
import { PageSkeleton } from './components/skeleton';
import { StatsCard } from './components/stats-card';
import { calculateHours } from './utils/calculate-hours';
import { exportToCsv } from './utils/export-csv';
import {
  filterByColaborador,
  filterByCourse,
  filterByDateRange,
} from './utils/filters';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { useLmsReport } from '@/data/corporate-university/course/report';
import type { Report } from '@/data/corporate-university/course/report/interfaces';
import { useSetTitle } from '@/hooks/use-set-title';
import { extractDuration, formatDate } from '@/utils/format-date';
import { cpfMask } from '@/utils/masks/cpf';

dayjs.locale('pt-br');

const title = 'Relatório Universidade Corporativa';

const statusOptions = [
  { value: 0, label: 'Selecione aqui' },
  { value: 1, label: 'Nome do curso' },
  { value: 2, label: 'Nome do colaborador' },
  { value: 3, label: 'Data de conclusão' },
];

const customLocaleText = {
  noRowsLabel: 'Nenhum registro encontrado',
  MuiTablePagination: {
    labelRowsPerPage: 'Linhas por página:',
  },
};

const columns: GridColDef<Report>[] = [
  {
    field: 'userName',
    headerName: 'Nome do colaborador',
    width: 200,
    sortable: true,
    valueGetter: (params) => params.row?.user?.name || '-',
  },
  {
    field: 'userCpf',
    headerName: 'CPF',
    sortable: true,
    valueGetter: (params) => params.row?.user?.cpf || '',
    renderCell: (params) => cpfMask(params.value) || '-',
  },
  {
    field: 'userEmail',
    headerName: 'Email',
    width: 230,
    sortable: true,
    valueGetter: (params) => params.row?.user?.email || '-',
  },
  {
    field: 'courseName',
    headerName: 'Nome do curso',
    width: 200,
    sortable: true,
    valueGetter: (params) => params.row?.course?.name || '-',
  },
  {
    field: 'progress',
    headerName: 'Progresso',
    sortable: true,
    valueGetter: (params) => params.row?.progress || 0,
    renderCell: (params) => `${params.value}%`,
  },
  {
    field: 'startDate',
    headerName: 'Data de início',
    sortable: true,
    valueGetter: (params) => params.row?.created_at || '',
    renderCell: (params) => formatDate(params.value),
  },
  {
    field: 'endDate',
    headerName: 'Data de conclusão',
    width: 110,
    sortable: true,
    valueGetter: (params) => params.row?.finished_time || null,
    renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
  },
  {
    field: 'duration',
    headerName: 'Carga horária',
    sortable: true,
    valueGetter: (params) => params.row?.course?.duration || '0:00:00',
    renderCell: (params) => extractDuration(params.value),
  },
];

export const LMSReport = () => {
  useSetTitle(title);
  const { report, isLoading } = useLmsReport();
  const [statusFilter, setStatusFilter] = useState(0);
  const [courseFilter, setCourseFilter] = useState('');
  const [colaboradorFilter, setColaboradorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  const filteredReport = useMemo((): Report[] => {
    if (!report || !Array.isArray(report)) return [];
    if (statusFilter === 0) return report;

    const filters: Record<number, Report[]> = {
      0: report,
      1: filterByCourse(report, courseFilter),
      2: filterByColaborador(report, colaboradorFilter),
      3: filterByDateRange(report, dateFrom, dateTo),
    };

    return filters[statusFilter] ?? report;
  }, [report, statusFilter, courseFilter, colaboradorFilter, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const completedCourses = filteredReport.filter(
      (course: Report) => !!course?.finished_time
    );

    return {
      totalHours: calculateHours(filteredReport),
      completedHours: calculateHours(completedCourses),
      totalEnrollments: filteredReport.length,
      completedEnrollments: completedCourses.length,
    };
  }, [filteredReport]);

  const handleCleanFilters = () => {
    setCourseFilter('');
    setColaboradorFilter('');
    setDateFrom(null);
    setDateTo(null);
    setStatusFilter(0);
  };

  const handleExport = () => {
    const filename = `lms-report-${dayjs().format('YYYY-MM-DD')}.csv`;
    exportToCsv(filteredReport, filename);
  };

  if (isLoading) return <PageSkeleton />;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <PageContainer>
        <PageHeader title={title} />

        <Grid container spacing={2}>
          <StatsCard
            title="Carga horária (total)"
            value={`${stats.totalHours} horas`}
          />
          <StatsCard
            title="Carga horária (concluídos)"
            value={`${stats.completedHours} horas`}
            success
          />
          <StatsCard
            title="Total de matrículas (realizadas)"
            value={stats.totalEnrollments}
          />
          <StatsCard
            title="Total de matrículas (concluídos)"
            value={stats.completedEnrollments}
            success
          />
        </Grid>

        <Stack spacing={2} pt={4}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="flex-end"
          >
            <Box sx={{ minWidth: { xs: '100%', md: 250 } }}>
              <FormControl fullWidth>
                <FormLabel>Filtrar por</FormLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as number)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {statusFilter === 1 && (
              <CourseFilter
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
              />
            )}

            {statusFilter === 2 && (
              <ColaboradorFilter
                value={colaboradorFilter}
                onChange={(e) => setColaboradorFilter(e.target.value)}
              />
            )}

            {statusFilter === 3 && (
              <DateRangeFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            )}

            {statusFilter !== 0 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ minWidth: { xs: '100%', md: 'auto' } }}
              >
                <Button variant="outlined" onClick={handleCleanFilters}>
                  Limpar
                </Button>
              </Stack>
            )}

            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="outlined"
                onClick={handleExport}
                disabled={!filteredReport || filteredReport.length === 0}
              >
                {statusFilter !== 0 ? 'Exportar seleção' : 'Exportar tudo'}
              </Button>
            </Box>
          </Stack>
        </Stack>

        <Box sx={{ width: '100%', mt: 2 }}>
          <DataGrid
            rows={filteredReport || []}
            columns={columns}
            getRowId={(row) => `${row.user_id}-${row.course_id}`}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            localeText={customLocaleText}
            sx={{ backgroundColor: '#FFFFFF' }}
          />
        </Box>
      </PageContainer>
    </LocalizationProvider>
  );
};
